// ไฟล์: frontend/src/pages/teacher/ScoreReports.js
import React, { useState, useEffect } from "react";
import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import Footer from "../../components/Footer";
import api from "../../services/api";
import Swal from "sweetalert2";
import Chart from "chart.js/auto";

function TeacherScoreReports() {
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [students, setStudents] = useState([]);
  const [scores, setScores] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // State สำหรับการกรอง
  const [selectedClassId, setSelectedClassId] = useState("");
  const [selectedSubjectId, setSelectedSubjectId] = useState("");
  const [selectedStudentId, setSelectedStudentId] = useState("");

  // State สำหรับกราฟ
  const [scoreChart, setScoreChart] = useState(null);

  useEffect(() => {
    fetchClasses();
    fetchSubjects();
  }, []);

  // เมื่อเลือกชั้นเรียนใหม่
  useEffect(() => {
    if (selectedClassId) {
      fetchStudentsInClass(selectedClassId);
    }
  }, [selectedClassId]);

  // เมื่อเลือกนักเรียนและวิชาใหม่
  useEffect(() => {
    if (selectedStudentId && selectedSubjectId) {
      fetchStudentScores();
    }
  }, [selectedStudentId, selectedSubjectId]);

  // Cleanup กราฟเมื่อ component unmount
  useEffect(() => {
    return () => {
      if (scoreChart) {
        scoreChart.destroy();
        setScoreChart(null);
      }
    };
  }, []);

  // ดึงข้อมูลชั้นเรียนที่ครูรับผิดชอบ
  const fetchClasses = async () => {
    try {
      setIsLoading(true);
      const response = await api.class.getMyClasses();
      const myClasses = response.data.data || [];
      setClasses(myClasses);

      // เลือกชั้นเรียนแรกเป็นค่าเริ่มต้น
      if (myClasses.length > 0) {
        setSelectedClassId(myClasses[0].id);
      }
    } catch (error) {
      console.error("Error fetching classes:", error);
      Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: "ไม่สามารถโหลดข้อมูลชั้นเรียนได้",
        confirmButtonColor: "#ff6b01",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // ดึงข้อมูลวิชาทั้งหมด
  const fetchSubjects = async () => {
    try {
      const response = await api.subject.getAllSubjects();
      setSubjects(response.data.data || []);

      // เลือกวิชาแรกเป็นค่าเริ่มต้น
      if (response.data.data && response.data.data.length > 0) {
        setSelectedSubjectId(response.data.data[0].id);
      }
    } catch (error) {
      console.error("Error fetching subjects:", error);
      Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: "ไม่สามารถโหลดข้อมูลวิชาได้",
        confirmButtonColor: "#ff6b01",
      });
    }
  };

  // ดึงข้อมูลนักเรียนในชั้น
  const fetchStudentsInClass = async (classId) => {
    try {
      const response = await api.student.getStudentsByClass(classId);
      const classStudents = response.data.data || [];
      setStudents(classStudents);

      // เลือกนักเรียนคนแรกเป็นค่าเริ่มต้น
      if (classStudents.length > 0) {
        setSelectedStudentId(classStudents[0].id);
      }
    } catch (error) {
      console.error("Error fetching students:", error);
      Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: "ไม่สามารถโหลดข้อมูลนักเรียนได้",
        confirmButtonColor: "#ff6b01",
      });
    }
  };

  // ดึงข้อมูลคะแนนของนักเรียนตามวิชา
  const fetchStudentScores = async () => {
    if (!selectedStudentId || !selectedSubjectId) return;

    try {
      setIsLoading(true);
      const response = await api.score.getScoresByStudentAndSubject(
        selectedStudentId,
        selectedSubjectId
      );
      const scoreData = response.data.data?.scores || [];
      const averageData = response.data.data?.average || {};

      setScores(scoreData);

      // สร้างกราฟคะแนน
      createScoreChart(scoreData, averageData);
    } catch (error) {
      console.error("Error fetching student scores:", error);
      Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: "ไม่สามารถโหลดข้อมูลคะแนนได้",
        confirmButtonColor: "#ff6b01",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // สร้างกราฟคะแนน
  const createScoreChart = (scoreData, averageData) => {
    // ทำลายกราฟเก่า (ถ้ามี)
    if (scoreChart) {
      scoreChart.destroy();
    }

    // ถ้าไม่มีข้อมูลคะแนน หรือไม่มี canvas ให้ออกจากฟังก์ชัน
    const canvas = document.getElementById("scoreChart");
    if (!canvas) {
      console.error("ไม่พบ canvas element สำหรับกราฟ");
      return;
    }

    // เตรียมข้อมูลสำหรับกราฟ
    const labels = scoreData.map((s) => s.indicator_code);
    const data = scoreData.map((s) => s.score);
    const fullScores = scoreData.map((s) => s.full_score);
    const scorePct = scoreData.map((s) => (s.score / s.full_score) * 100);

    // สร้างกราฟใหม่
    const newChart = new Chart(canvas, {
      type: "bar",
      data: {
        labels: labels,
        datasets: [
          {
            label: "คะแนนที่ได้",
            data: data,
            backgroundColor: "rgba(255, 107, 1, 0.7)",
            borderColor: "rgba(255, 107, 1, 1)",
            borderWidth: 1,
            yAxisID: "y",
          },
          {
            label: "คะแนนเต็ม",
            data: fullScores,
            backgroundColor: "rgba(200, 200, 200, 0.5)",
            borderColor: "rgba(200, 200, 200, 1)",
            borderWidth: 1,
            type: "line",
            yAxisID: "y",
          },
          {
            label: "เปอร์เซ็นต์",
            data: scorePct,
            backgroundColor: "rgba(75, 192, 192, 0.7)",
            borderColor: "rgba(75, 192, 192, 1)",
            borderWidth: 1,
            type: "line",
            yAxisID: "y1",
          },
        ],
      },
      options: {
        scales: {
          y: {
            type: "linear",
            display: true,
            position: "left",
            title: {
              display: true,
              text: "คะแนน",
            },
          },
          y1: {
            type: "linear",
            display: true,
            position: "right",
            title: {
              display: true,
              text: "เปอร์เซ็นต์",
            },
            min: 0,
            max: 100,
            grid: {
              drawOnChartArea: false,
            },
          },
          x: {
            title: {
              display: true,
              text: "ตัวชี้วัด",
            },
          },
        },
        plugins: {
          title: {
            display: true,
            text: "คะแนนตามตัวชี้วัดของนักเรียน",
          },
        },
      },
    });

    setScoreChart(newChart);
  };

  // ฟังก์ชั่นออกรายงานเป็น PDF (จำลอง)
  const exportReport = () => {
    if (!selectedStudentId || !selectedSubjectId || scores.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "ไม่สามารถออกรายงานได้",
        text: "กรุณาเลือกนักเรียนและวิชา และตรวจสอบว่ามีข้อมูลคะแนน",
        confirmButtonColor: "#ff6b01",
      });
      return;
    }

    Swal.fire({
      title: "กำลังออกรายงาน",
      text: "กำลังจัดเตรียมไฟล์ PDF...",
      icon: "info",
      timer: 1500,
      timerProgressBar: true,
      showConfirmButton: false,
    }).then(() => {
      Swal.fire({
        icon: "success",
        title: "ออกรายงานสำเร็จ",
        text: "รายงานถูกดาวน์โหลดลงเครื่องของคุณแล้ว",
        confirmButtonColor: "#ff6b01",
      });
    });
  };

  // คำนวณคะแนนรวมและเปอร์เซ็นต์
  const calculateTotalScore = () => {
    if (scores.length === 0) return { total: 0, fullTotal: 0, percentage: 0 };

    const total = scores.reduce(
      (sum, item) => sum + (parseFloat(item.score) || 0),
      0
    );
    const fullTotal = scores.reduce(
      (sum, item) => sum + (parseFloat(item.full_score) || 0),
      0
    );
    const percentage = fullTotal > 0 ? (total / fullTotal) * 100 : 0;

    return { total, fullTotal, percentage };
  };

  // คำนวณคะแนนรวมถ่วงน้ำหนัก
  const calculateWeightedScore = () => {
    if (scores.length === 0)
      return { weighted: 0, maxWeighted: 0, percentage: 0 };

    const weightedTotal = scores.reduce(
      (sum, item) =>
        sum + (parseFloat(item.score) || 0) * (parseFloat(item.weight) || 1),
      0
    );

    const maxWeightedTotal = scores.reduce(
      (sum, item) =>
        sum +
        (parseFloat(item.full_score) || 0) * (parseFloat(item.weight) || 1),
      0
    );

    const percentage =
      maxWeightedTotal > 0 ? (weightedTotal / maxWeightedTotal) * 100 : 0;

    return {
      weighted: weightedTotal,
      maxWeighted: maxWeightedTotal,
      percentage,
    };
  };

  // แปลงเกรดตามเปอร์เซ็นต์
  const calculateGrade = (percentage) => {
    if (percentage >= 80) return "4";
    if (percentage >= 75) return "3.5";
    if (percentage >= 70) return "3";
    if (percentage >= 65) return "2.5";
    if (percentage >= 60) return "2";
    if (percentage >= 55) return "1.5";
    if (percentage >= 50) return "1";
    return "0";
  };

  // รายละเอียดคะแนน
  const totalScore = calculateTotalScore();
  const weightedScore = calculateWeightedScore();
  const grade = calculateGrade(weightedScore.percentage);

  return (
    <div className="wrapper">
      <Header />
      <Sidebar />

      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0">รายงานคะแนนเก็บรายตัวชี้วัด</h1>
              </div>
              <div className="col-sm-6">
                <ol className="breadcrumb float-sm-right">
                  <li className="breadcrumb-item">
                    <a href="/teacher/dashboard">หน้าหลัก</a>
                  </li>
                  <li className="breadcrumb-item active">รายงานคะแนนเก็บ</li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        <section className="content">
          <div className="container-fluid">
            {isLoading ? (
              <div className="loading-spinner">
                <i className="fas fa-spinner fa-spin"></i>
              </div>
            ) : (
              <>
                {/* ตัวเลือกรายงาน */}
                <div className="card card-orange">
                  <div className="card-header">
                    <h3 className="card-title">ตัวเลือกรายงาน</h3>
                  </div>
                  <div className="card-body">
                    {classes.length === 0 ? (
                      <div className="alert alert-warning">
                        <i className="fas fa-exclamation-triangle mr-2"></i>
                        คุณยังไม่ได้รับมอบหมายให้ดูแลชั้นเรียนใดๆ
                        กรุณาติดต่อผู้ดูแลระบบ
                      </div>
                    ) : (
                      <div className="row">
                        <div className="col-md-4">
                          <div className="form-group">
                            <label>ชั้นเรียน</label>
                            <select
                              className="form-control"
                              value={selectedClassId}
                              onChange={(e) =>
                                setSelectedClassId(e.target.value)
                              }
                            >
                              {classes.map((classItem) => (
                                <option key={classItem.id} value={classItem.id}>
                                  {classItem.class_name} (ห้อง{" "}
                                  {classItem.room_number})
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="form-group">
                            <label>นักเรียน</label>
                            <select
                              className="form-control"
                              value={selectedStudentId}
                              onChange={(e) =>
                                setSelectedStudentId(e.target.value)
                              }
                            >
                              {students.length === 0 ? (
                                <option value="">-- ไม่พบนักเรียน --</option>
                              ) : (
                                students.map((student) => (
                                  <option key={student.id} value={student.id}>
                                    {student.first_name} {student.last_name}
                                  </option>
                                ))
                              )}
                            </select>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="form-group">
                            <label>วิชา</label>
                            <select
                              className="form-control"
                              value={selectedSubjectId}
                              onChange={(e) =>
                                setSelectedSubjectId(e.target.value)
                              }
                            >
                              {subjects.map((subject) => (
                                <option key={subject.id} value={subject.id}>
                                  {subject.subject_code} -{" "}
                                  {subject.subject_name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div className="col-md-12 mt-2">
                          <button
                            className="btn btn-orange"
                            onClick={fetchStudentScores}
                            disabled={!selectedStudentId || !selectedSubjectId}
                          >
                            <i className="fas fa-search mr-1"></i> แสดงรายงาน
                          </button>

                          <button
                            className="btn btn-success ml-2"
                            onClick={exportReport}
                            disabled={scores.length === 0}
                          >
                            <i className="fas fa-file-pdf mr-1"></i> ออกรายงาน
                            PDF
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* แสดงรายงานคะแนน */}
                {scores.length > 0 && (
                  <div className="row">
                    <div className="col-md-8">
                      <div className="card card-orange">
                        <div className="card-header">
                          <h3 className="card-title">
                            รายงานคะแนน:{" "}
                            {students.find(
                              (s) => s.id === parseInt(selectedStudentId)
                            )?.first_name || ""}{" "}
                            {students.find(
                              (s) => s.id === parseInt(selectedStudentId)
                            )?.last_name || ""}
                          </h3>
                          <div className="card-tools">
                            <button
                              className="btn btn-tool"
                              onClick={exportReport}
                            >
                              <i className="fas fa-file-pdf"></i> ออกรายงาน PDF
                            </button>
                          </div>
                        </div>
                        <div className="card-body">
                          <div className="table-responsive">
                            <table className="table table-bordered table-striped">
                              <thead>
                                <tr>
                                  <th style={{ width: "40px" }}>ลำดับ</th>
                                  <th style={{ width: "100px" }}>
                                    รหัสตัวชี้วัด
                                  </th>
                                  <th>ชื่อตัวชี้วัด</th>
                                  <th style={{ width: "80px" }}>น้ำหนัก</th>
                                  <th style={{ width: "80px" }}>คะแนนเต็ม</th>
                                  <th style={{ width: "80px" }}>คะแนนที่ได้</th>
                                  <th style={{ width: "80px" }}>ร้อยละ</th>
                                </tr>
                              </thead>
                              <tbody>
                                {scores.length === 0 ? (
                                  <tr>
                                    <td colSpan="7" className="text-center">
                                      ไม่พบข้อมูลคะแนน
                                    </td>
                                  </tr>
                                ) : (
                                  scores.map((score, index) => (
                                    <tr key={score.id}>
                                      <td className="text-center">
                                        {index + 1}
                                      </td>
                                      <td>{score.indicator_code}</td>
                                      <td>{score.indicator_name}</td>
                                      <td className="text-center">
                                        {score.weight}
                                      </td>
                                      <td className="text-center">
                                        {score.full_score}
                                      </td>
                                      <td className="text-center">
                                        {score.score}
                                      </td>
                                      <td className="text-center">
                                        {(
                                          (score.score / score.full_score) *
                                          100
                                        ).toFixed(2)}
                                        %
                                      </td>
                                    </tr>
                                  ))
                                )}
                              </tbody>
                              <tfoot>
                                <tr className="bg-light">
                                  <th colSpan="4">รวม</th>
                                  <th className="text-center">
                                    {totalScore.fullTotal.toFixed(2)}
                                  </th>
                                  <th className="text-center">
                                    {totalScore.total.toFixed(2)}
                                  </th>
                                  <th className="text-center">
                                    {totalScore.percentage.toFixed(2)}%
                                  </th>
                                </tr>
                                <tr className="bg-light">
                                  <th colSpan="4">รวม (ถ่วงน้ำหนัก)</th>
                                  <th className="text-center">
                                    {weightedScore.maxWeighted.toFixed(2)}
                                  </th>
                                  <th className="text-center">
                                    {weightedScore.weighted.toFixed(2)}
                                  </th>
                                  <th className="text-center">
                                    {weightedScore.percentage.toFixed(2)}%
                                  </th>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-4">
                      {/* <div className="card card-orange">
                        <div className="card-header">
                          <h3 className="card-title">แผนภูมิคะแนน</h3>
                        </div>
                        <div className="card-body">
                          <canvas id="scoreChart"></canvas>
                        </div>
                      </div> */}

                      <div className="card card-orange">
                        <div className="card-header">
                          <h3 className="card-title">สรุปผลการเรียน</h3>
                        </div>
                        <div className="card-body">
                          <div className="text-center mb-3">
                            <h1 className="display-4">{grade}</h1>
                            <p className="text-muted">เกรด</p>
                          </div>

                          <table className="table table-bordered">
                            <tbody>
                              <tr>
                                <th>นักเรียน</th>
                                <td>
                                  {students.find(
                                    (s) => s.id === parseInt(selectedStudentId)
                                  )?.first_name || ""}{" "}
                                  {students.find(
                                    (s) => s.id === parseInt(selectedStudentId)
                                  )?.last_name || ""}
                                </td>
                              </tr>
                              <tr>
                                <th>วิชา</th>
                                <td>
                                  {subjects.find(
                                    (s) => s.id === parseInt(selectedSubjectId)
                                  )?.subject_name || ""}
                                </td>
                              </tr>
                              <tr>
                                <th>คะแนนรวม</th>
                                <td>
                                  {totalScore.total.toFixed(2)} /{" "}
                                  {totalScore.fullTotal.toFixed(2)}
                                </td>
                              </tr>
                              <tr>
                                <th>คะแนนถ่วงน้ำหนัก</th>
                                <td>
                                  {weightedScore.weighted.toFixed(2)} /{" "}
                                  {weightedScore.maxWeighted.toFixed(2)}
                                </td>
                              </tr>
                              <tr>
                                <th>ร้อยละ</th>
                                <td>{weightedScore.percentage.toFixed(2)}%</td>
                              </tr>
                              <tr>
                                <th>จำนวนตัวชี้วัด</th>
                                <td>{scores.length}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div className="card card-orange">
                        <div className="card-header">
                          <h3 className="card-title">เกณฑ์การให้เกรด</h3>
                        </div>
                        <div className="card-body p-0">
                          <table className="table table-striped mb-0">
                            <thead>
                              <tr className="bg-light">
                                <th>ช่วงคะแนน</th>
                                <th>เกรด</th>
                                <th>ความหมาย</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td>80 - 100%</td>
                                <td>4</td>
                                <td>ดีเยี่ยม</td>
                              </tr>
                              <tr>
                                <td>75 - 79.99%</td>
                                <td>3.5</td>
                                <td>ดีมาก</td>
                              </tr>
                              <tr>
                                <td>70 - 74.99%</td>
                                <td>3</td>
                                <td>ดี</td>
                              </tr>
                              <tr>
                                <td>65 - 69.99%</td>
                                <td>2.5</td>
                                <td>ค่อนข้างดี</td>
                              </tr>
                              <tr>
                                <td>60 - 64.99%</td>
                                <td>2</td>
                                <td>ปานกลาง</td>
                              </tr>
                              <tr>
                                <td>55 - 59.99%</td>
                                <td>1.5</td>
                                <td>พอใช้</td>
                              </tr>
                              <tr>
                                <td>50 - 54.99%</td>
                                <td>1</td>
                                <td>ผ่านเกณฑ์ขั้นต่ำ</td>
                              </tr>
                              <tr>
                                <td>0 - 49.99%</td>
                                <td>0</td>
                                <td>ไม่ผ่านเกณฑ์</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}

export default TeacherScoreReports;
